import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ChatProvider } from './contexts/ChatContext';
import socketService from './services/socketService';
import LoginScreen from './screens/LoginScreen';
import RegisterScreen from './screens/RegisterScreen';
import HomeScreen from './screens/HomeScreen';
import ChatScreen from './screens/ChatScreen';
import NewChatScreen from './screens/NewChatScreen';
import CallScreen from './screens/CallScreen';
import ConnectivityBanner from './components/ConnectivityBanner';
import apiService from './services/apiService';
import webPushService from './services/webPushService';
import { playRingtone, stopRingtone } from './services/audioUtil';
import './App.css';

function AuthGate({ children }) {
  const { isLoggedIn, loading } = useAuth();
  if (loading) {
    return (
      <div className="loading-center" style={{ height: '100vh' }}>
        <div className="spinner" />
      </div>
    );
  }
  return isLoggedIn ? children : <Navigate to="/login" replace />;
}

function GuestGate({ children }) {
  const { isLoggedIn, loading } = useAuth();
  if (loading) {
    return (
      <div className="loading-center" style={{ height: '100vh' }}>
        <div className="spinner" />
      </div>
    );
  }
  return !isLoggedIn ? children : <Navigate to="/" replace />;
}

function IncomingCallHandler({ children }) {
  const navigate = useNavigate();
  const [incomingCall, setIncomingCall] = useState(null);

  const handleCallOffer = useCallback((data) => {
    playRingtone();
    const from = data?.fromId || data?.from;
    apiService
      .getUser(from)
      .then((u) => {
        setIncomingCall({ ...data, fromName: u?.displayName || u?.username || 'Someone' });
      })
      .catch(() => {
        setIncomingCall({ ...data, fromName: 'Someone' });
      });
  }, []);

  const handleCallEnd = useCallback(() => {
    stopRingtone();
    setIncomingCall(null);
  }, []);

  // Handle a call push received while the tab is open.
  const handlePushCall = useCallback(
    (data) => {
      if (data && data.type === 'call' && (data.fromId || data.from)) {
        handleCallOffer(data);
      }
    },
    [handleCallOffer]
  );

  const answerParams = useCallback(
    (params) => {
      const from = params.get('from');
      if (!from) return;
      navigate(`/call/${from}`, {
        state: {
          isVideo: params.get('video') === 'true',
          isCaller: false,
          offerSdp: params.get('sdp') || '',
        },
      });
    },
    [navigate]
  );

  const declineFromParams = useCallback((params) => {
    const from = params.get('from');
    if (!from) return;
    socketService.endCall(from);
    stopRingtone();
  }, []);

  const handleActionUrl = useCallback(
    (url) => {
      const u = new URL(url, window.location.origin);
      const params = u.searchParams;
      const action = params.get('callAction');
      if (action === 'answer') answerParams(params);
      else if (action === 'decline') declineFromParams(params);
    },
    [answerParams, declineFromParams]
  );

  useEffect(() => {
    socketService.onCallOffer = handleCallOffer;
    socketService.onCallEnd = handleCallEnd;
    webPushService.onForegroundMessage(handlePushCall);
    webPushService.onAction(handleActionUrl);

    // If this tab was opened by clicking a notification (app was closed),
    // the call action is in the URL query string.
    const params = new URLSearchParams(window.location.search);
    if (params.get('callAction')) {
      // Give the app a moment to auto-login + connect the socket first.
      setTimeout(() => handleActionUrl(window.location.href), 800);
    }
    return () => {
      stopRingtone();
      socketService.onCallOffer = null;
      socketService.onCallEnd = null;
      webPushService.foregroundCallback = null;
      webPushService.actionCallback = null;
    };
  }, [handleCallOffer, handleCallEnd, handlePushCall, handleActionUrl]);

  const acceptCall = () => {
    stopRingtone();
    const data = incomingCall;
    setIncomingCall(null);
    navigate(`/call/${incomingCall.fromId || incomingCall.from}`, {
      state: { isVideo: data.video, isCaller: false, offerSdp: data.sdp },
    });
  };

  const declineCall = () => {
    stopRingtone();
    socketService.endCall(incomingCall.fromId || incomingCall.from);
    setIncomingCall(null);
  };

  return (
    <>
      {incomingCall && (
        <div className="incoming-overlay">
          <div className="incoming-dialog">
            <div className="incoming-avatar">
              {incomingCall.fromName ? incomingCall.fromName[0].toUpperCase() : '📞'}
            </div>
            <div className="incoming-title">
              {incomingCall.video ? '📹' : '📞'} Incoming{' '}
              {incomingCall.video ? 'Video' : 'Voice'} Call
            </div>
            <div className="incoming-subtitle">
              {incomingCall.fromName} is calling...
            </div>
            <div className="incoming-actions">
              <button className="incoming-decline" onClick={declineCall}>
                Decline
              </button>
              <button className="incoming-answer" onClick={acceptCall}>
                {incomingCall.video ? '📹' : '📞'} Answer
              </button>
            </div>
          </div>
        </div>
      )}
      {children}
    </>
  );
}

function AppContent() {
  return (
    <IncomingCallHandler>
      <ConnectivityBanner />
      <Routes>
        <Route
          path="/login"
          element={
            <GuestGate>
              <LoginScreen />
            </GuestGate>
          }
        />
        <Route
          path="/register"
          element={
            <GuestGate>
              <RegisterScreen />
            </GuestGate>
          }
        />
        <Route
          path="/"
          element={
            <AuthGate>
              <ChatProvider>
                <HomeScreen />
              </ChatProvider>
            </AuthGate>
          }
        />
        <Route
          path="/chat/:id"
          element={
            <AuthGate>
              <ChatProvider>
                <ChatScreen />
              </ChatProvider>
            </AuthGate>
          }
        />
        <Route
          path="/new-chat"
          element={
            <AuthGate>
              <ChatProvider>
                <NewChatScreen />
              </ChatProvider>
            </AuthGate>
          }
        />
        <Route
          path="/call/:targetUserId"
          element={
            <AuthGate>
              <CallScreen />
            </AuthGate>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </IncomingCallHandler>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
